/**
 * Shop names are user-supplied and vary wildly in length — "Rebels 108" next
 * to "NINETYSEVENTWELVE studio". At one fixed size the long ones crowd the
 * row they sit in (on the listing header they run into the share/save
 * buttons) or wrap onto a second line that pushes the city label down.
 *
 * The rule: past LONG_NAME_CHARS, step the type down 15%. One step, not a
 * sliding scale — continuous shrinking makes every shop's title a slightly
 * different size, which reads as sloppy rather than responsive.
 *
 * Returned as an inline style on purpose: 15% of a base lands on values like
 * 1.0625rem that have no Tailwind step, and arbitrary `text-[Npx]` classes
 * are easy to typo into silence.
 */

/** Longest name that keeps the full-size treatment. */
export const LONG_NAME_CHARS = 20

/** How much a long name shrinks by. */
export const LONG_NAME_SCALE = 0.85

export function isLongShopName(name: string | null | undefined): boolean {
  return (name ?? "").trim().length > LONG_NAME_CHARS
}

/**
 * Inline font-size for a shop name.
 *
 * @param name    the shop name being rendered
 * @param baseRem the size it would use if it were short (1.25 = Tailwind text-xl)
 *
 * @example
 *   <h1 style={shopNameStyle(shop.name, 1.25)}>{shop.name}</h1>
 */
export function shopNameStyle(
  name: string | null | undefined,
  baseRem: number,
): { fontSize: string } {
  const rem = isLongShopName(name) ? baseRem * LONG_NAME_SCALE : baseRem
  // Trim float noise (1.0625 stays exact; 0.85 * 1.5 would not).
  return { fontSize: `${Number(rem.toFixed(4))}rem` }
}
